/**
 * Time-aware proxy weighting.
 *
 * Lightweight modifier on the existing pleasantness score that nudges routing
 * based on *predictable* time-of-day patterns — NOT live traffic. We have no
 * real-time congestion feed; this is purely a heuristic that says "at 5pm on
 * a weekday, arterials and commercial corridors tend to be unpleasant, so
 * shave a few points off routes that lean on them."
 *
 * The effect is intentionally moderate (cap ±10 pts) so routes never become
 * strange or wildly detoured just because the user opened the app at 5pm.
 *
 * Windows (local time):
 *   morning_commute  Mon-Fri 07:00–09:30
 *   school_pickup    Mon-Fri 14:30–16:00
 *   evening_commute  Mon-Fri 16:30–18:30
 *   night            any day  21:00–05:30
 *   weekend_daytime  Sat/Sun  10:00–18:00
 */
import type { FeatureSummary } from "@/utils/scoring";

export type TimeWindow =
  | "morning_commute"
  | "school_pickup"
  | "evening_commute"
  | "night"
  | "weekend_daytime"
  | "off_peak";

/** Resolve the current time window in the user's local timezone. */
export function getTimeWindow(now: Date = new Date()): TimeWindow {
  const day = now.getDay(); // 0=Sun, 6=Sat
  const minutes = now.getHours() * 60 + now.getMinutes();
  const isWeekend = day === 0 || day === 6;
  const isWeekday = !isWeekend;

  // Night spans midnight — check both sides.
  if (minutes >= 21 * 60 || minutes < 5 * 60 + 30) return "night";

  if (isWeekday) {
    if (minutes >= 7 * 60 && minutes < 9 * 60 + 30) return "morning_commute";
    if (minutes >= 14 * 60 + 30 && minutes < 16 * 60) return "school_pickup";
    if (minutes >= 16 * 60 + 30 && minutes < 18 * 60 + 30) return "evening_commute";
  }

  if (isWeekend && minutes >= 10 * 60 && minutes < 18 * 60) return "weekend_daytime";

  return "off_peak";
}

/** Short, user-facing label for an evidence chip. */
export function timeWindowLabel(w: TimeWindow): string {
  switch (w) {
    case "morning_commute": return "morning rush";
    case "evening_commute": return "evening rush";
    case "school_pickup":   return "school pickup hour";
    case "night":           return "late-night";
    case "weekend_daytime": return "weekend daytime";
    default: return "";
  }
}

export interface TimeAwareResult {
  window: TimeWindow;
  /** Adjustment in pleasantness points. Clamped to ±10 off-commute, ±15
   *  during morning/evening commute windows (#64) so rush-hour reality
   *  can actually decide a route. */
  delta: number;
  /** Optional evidence chip text when |delta| ≥ 2. */
  chip?: string;
}

/**
 * Compute the time-aware adjustment for a single route's feature summary.
 * Returns a delta to ADD to the pleasantness score (can be negative).
 *
 * All weights are deliberately small — we want a nudge, not a takeover.
 */
export function timeAwareAdjustment(
  features: FeatureSummary,
  now: Date = new Date(),
): TimeAwareResult {
  const window = getTimeWindow(now);
  if (window === "off_peak") return { window, delta: 0 };

  const f = features;
  const namedParks   = (f.parkNames?.length ?? 0) + (f.natureNames?.length ?? 0);
  const namedWater   = f.waterNames?.length ?? 0;
  const namedHistoric= f.historicNames?.length ?? 0;
  // Rough "commercial corridor" proxy: dense cafés + shops + landmarks
  // typically signals a busy retail/restaurant strip.
  const commercialDensity = f.cafes + f.shops + f.landmarks * 0.5;

  let delta = 0;

  switch (window) {
    case "morning_commute":
    case "evening_commute": {
      // Commute windows: arterials/highways are predictably grim; residential
      // backstreets and park/water-adjacent roads tend to be calmer.
      //
      // Real-drive feedback (#64): the previous weights were nudges, not
      // tiebreakers. A route that snaked through downtown Tarrytown at
      // 5:30pm still beat the reservoir-adjacent backroads because the
      // commercial penalty capped at -4 and the arterial penalty at -7,
      // and the cumulative ±10 clamp at the bottom of the function then
      // clipped anything stronger. Strengthened so the penalty can
      // actually decide a route during rush hour — the user has told us
      // the time-of-day context is the whole point of the time-aware
      // layer.
      delta -= f.highwayPct  * 0.07;        // up to ~-7 on highway-heavy
      // Town main streets / arterials during rush — the road class most
      // likely to be at a standstill at 5:30pm. Real-drive note: "during
      // commute times, you should avoid the main thoroughfare like a
      // main street in a town."
      delta -= f.majorRoadPct * 0.10;       // up to ~-10 on arterial-heavy
      // Commercial corridor penalty — uncapped here; the global ±15
      // clamp below catches it. A route that runs five blocks of
      // restaurants + shops + landmarks during evening rush IS that
      // bad in practice.
      delta -= commercialDensity * 0.35;
      delta += (f.residentialPct - 40) * 0.06; // ~ -2.4 .. +3.6
      delta += Math.min(3, namedParks * 0.9);
      delta += Math.min(2.5, namedWater * 0.8);
      break;
    }

    case "school_pickup": {
      // We don't yet ingest school/daycare/playground points, so we proxy
      // school-zone congestion via major-road and arterial share — those are
      // where pickup queues spill onto. Small effect by design.
      delta -= f.majorRoadPct * 0.035;
      delta -= f.highwayPct   * 0.02;
      delta += Math.min(2, (f.residentialPct - 50) * 0.03);
      break;
    }

    case "night": {
      // Nighttime: isolated parks/trails feel exposed; moderately active
      // residential and corridors with cafés/shops feel safer.
      delta -= Math.min(5, namedParks * 1.2);          // isolated parks penalty
      delta -= Math.min(3, f.waterPct * 0.04);         // remote waterfront
      delta += (f.residentialPct - 35) * 0.04;         // ~ -1.4 .. +2.6
      delta += Math.min(3, commercialDensity * 0.25);  // open-business signal
      break;
    }

    case "weekend_daytime": {
      // Weekend daytime: scenic/recreational shines; dense restaurant strips
      // tend to clog and get noisy.
      delta += Math.min(3, namedParks    * 0.9);
      delta += Math.min(2.5, namedWater  * 0.8);
      delta += Math.min(2.5, namedHistoric * 0.9);
      delta += Math.min(2, f.waterPct * 0.03);
      delta -= Math.min(3, commercialDensity * 0.2);
      break;
    }
  }

  // Clamp the total. Commute windows get a wider band (#64) because rush-
  // hour congestion really CAN single-handedly make a route worse than
  // its raw pleasantness score would suggest — that's the whole point of
  // this layer. Off-commute windows keep the gentler nudge.
  const isCommute =
    window === "morning_commute" || window === "evening_commute";
  const cap = isCommute ? 15 : 10;
  const clamped = Math.max(-cap, Math.min(cap, delta));

  // Positive-only cards: surface the GOOD time-of-day fit, never the negative
  // "Heavier going during …". The congestion still influences ranking via
  // `delta` above — we just don't put a downbeat chip on the card. (Pleasant's
  // voice: show what's good on the route, not what isn't there.)
  let chip: string | undefined;
  if (clamped >= 2) {
    chip = `Good fit for ${timeWindowLabel(window)}`;
  }

  return { window, delta: clamped, chip };
}
